import { ADT } from "pb.adt";
import { IsAiTutorId, IsAiTutorUserId } from "@/helpers/aiTutor.ts";
import type { FlowGeneratorSession } from "@/types/flow.ts";
import type { HeldReplyState } from "@/stores/useChatStore.ts";

/**
 * The cache key for a session's pending-AI-reply. Shared (deliberately) with
 * TutorWrapper's claimed-chat llmMessage bootstrap — always build it from this
 * factory so invalidations/resets can't silently drift from the query.
 */
export const chatResponseQueryKey = (flowGeneratorSessionId: number) =>
  ["getChatResponse", flowGeneratorSessionId] as const;

/**
 * THE recognition seam for a safety-paused session in the queue: the AI paused
 * itself over a flagged message (student's or its own draft) and released the
 * chat into the ordinary round-robin. The payload shape is the contract:
 * auto-moderated, currently unowned, previously owned by the AI tutor. Such a
 * row stays claimable exactly like any other unowned row (plain claim).
 */
export const isPausedForReview = (session: {
  isAutoModerated?: boolean | null;
  tutorId?: number | null;
  tutorUserId?: number | null;
  previousTutorId?: number | null;
}): boolean =>
  session.isAutoModerated === true &&
  // Unowned arrives as 0 as well as null (falsy check, same as the queue filters)
  !session.tutorId &&
  !session.tutorUserId &&
  IsAiTutorId(session.previousTutorId);

/**
 * The round-robin ASSIGNED safety review: the hold handed this session to one
 * reviewer instead of releasing it unclaimed. The session's tutorUserId is the
 * reviewer's USER id (compare useUserStore().userId, never a tutor id), and
 * the latest OPEN intervention row still carries the hold fingerprint —
 * auto-moderated, AI paused (isAutoResponseEnabled false), bot joined,
 * previously the AI tutor. A claimed chat fails this (isAutoResponseEnabled
 * flips true on claim/resume), and payloads without the nested rows (older
 * backend) simply return false — the unassigned path is untouched.
 */
export const isHeldAssignedToMe = (
  session:
    | {
        tutorUserId?: number | null;
        flowGeneratorSessionInterventions?: Array<{
          flowGeneratorSessionInterventionId: number;
          dateEnded?: string | null;
          isAutoModerated?: boolean | null;
          isAutoResponseEnabled?: boolean | null;
          botJoined?: boolean | null;
          previousTutorId?: number | null;
        }> | null;
      }
    | null
    | undefined,
  userId: number,
): boolean => {
  if (!session || !userId || session.tutorUserId !== userId) return false;

  const latestOpen = session.flowGeneratorSessionInterventions
    // Open = dateEnded null per contract (== also tolerates rows omitting the field)
    ?.filter((intervention) => intervention.dateEnded == null)
    .sort((a, b) => b.flowGeneratorSessionInterventionId - a.flowGeneratorSessionInterventionId)[0];

  return (
    !!latestOpen &&
    latestOpen.isAutoModerated === true &&
    latestOpen.isAutoResponseEnabled === false &&
    latestOpen.botJoined === true &&
    IsAiTutorUserId(latestOpen.previousTutorId)
  );
};

/**
 * Whether a queue row should carry the "needs review" treatment. Kept here so
 * every consumer (queue card, Nav counter) shares one definition. Primary
 * signal is the session payload's pause shape; the per-row response probe
 * remains as the fallback for payloads not yet carrying the new fields (and
 * for a flag that lands while the AI is still marked as serving).
 */
export const isSessionFlagged = (
  session: Partial<FlowGeneratorSession>,
  heldStates: Map<number, HeldReplyState>,
): boolean => {
  const held = session.flowGeneratorSessionId
    ? heldStates.get(session.flowGeneratorSessionId)
    : undefined;
  return isPausedForReview(session) || (!!held && ADT.match(held, "held"));
};
